import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import Header from "../components/Header";
import { subscriberApi } from "../lib/api";

export default function SubscriberLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("no_subscription");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await subscriberApi.login(email.trim().toLowerCase(), password);
      localStorage.setItem("lexhab_sub_token", data.access_token);
      localStorage.setItem(
        "lexhab_sub_info",
        JSON.stringify({ email: data.email, plan: data.plan, expires_at: data.expires_at })
      );
      nav("/account");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Identifiants incorrects. Veuillez réessayer.";
      
      // Check for special error codes
      if (detail.startsWith("NO_SUBSCRIPTION|")) {
        setModalMessage(detail.split("|")[1]);
        setModalType("no_subscription");
        setShowSubscriptionModal(true);
      } else if (detail.startsWith("SUBSCRIPTION_EXPIRED|")) {
        setModalMessage(detail.split("|")[1]);
        setModalType("subscription_expired");
        setShowSubscriptionModal(true);
      } else {
        setError(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowSubscriptionModal(false);
    setModalMessage("");
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="subscriber-login-page">
      <Header />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-primary/15 rounded-sm shadow-lg p-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
            / espace abonné
          </div>
          <h1 className="font-display text-2xl font-semibold text-primary mb-1">
            Connexion abonné
          </h1>
          <p className="text-sm text-foreground/60 mb-6">
            Accédez à Lexihab avec les identifiants reçus lors de votre abonnement.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <Field
              id="email"
              icon={Mail}
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              testid="sub-email-input"
            />
            <Field
              id="password"
              icon={Lock}
              label="Mot de passe"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              testid="sub-password-input"
            />

            {error && (
              <div className="text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white px-5 py-3 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
              data-testid="sub-login-submit"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
              Se connecter
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/subscriber/forgot-password" className="text-xs text-foreground/60 hover:text-primary">
              Mot de passe oublié ?
            </Link>
          </div>

          <div className="mt-6 pt-5 border-t border-primary/10 text-center">
            <p className="text-xs text-foreground/50 mb-2">Pas encore de compte ?</p>
            <Link
              to="/subscriber/register"
              className="text-xs font-medium text-primary hover:underline"
            >
              Créer un compte →
            </Link>
          </div>

          <div className="mt-4 text-center">
            <Link to="/pricing" className="text-xs text-foreground/60 hover:text-primary">
              Voir les offres d'abonnement →
            </Link>
          </div>
        </div>
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden animate-slide-up">
            <div className={`px-6 py-4 ${modalType === "no_subscription" ? "bg-gradient-to-r from-blue-50 to-indigo-50" : "bg-gradient-to-r from-amber-50 to-orange-50"}`}>
              <div className="flex items-center gap-3">
                {modalType === "no_subscription" ? (
                  <AlertCircle className="w-6 h-6 text-blue-600" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                )}
                <h3 className="font-display text-lg font-semibold text-gray-800">
                  {modalType === "no_subscription" ? "Abonnement requis" : "Abonnement expiré"}
                </h3>
              </div>
            </div>
            
            <div className="px-6 py-5">
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {modalMessage}
              </p>
              
              <div className={`p-3 rounded-md mb-4 ${modalType === "no_subscription" ? "bg-blue-50 border border-blue-100" : "bg-amber-50 border border-amber-100"}`}>
                <div className="flex items-start gap-2">
                  <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${modalType === "no_subscription" ? "text-blue-600" : "text-amber-600"}`} />
                  <p className="text-xs text-gray-700">
                    <strong>Important :</strong> Utilisez la même adresse email que celle utilisée lors de la création de votre compte pour souscrire à un abonnement. Cela permet à la plateforme d'associer automatiquement votre abonnement à votre compte.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  onClick={() => nav("/pricing")}
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    modalType === "no_subscription"
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-amber-600 text-white hover:bg-amber-700"
                  }`}
                >
                  {modalType === "no_subscription" ? "Voir les offres d'abonnement" : "Renouveler mon abonnement"}
                </button>
                <button
                  onClick={closeModal}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ id, icon: Icon, label, testid, ...rest }) {
  return (
    <div>
      <label htmlFor={id} className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1.5">
        {label}
      </label>
      <div className="flex items-center gap-2 border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors bg-white">
        <Icon size={14} className="text-foreground/40" />
        <input
          id={id}
          required
          className="flex-1 bg-transparent text-sm py-2.5 outline-none"
          data-testid={testid}
          {...rest}
        />
      </div>
    </div>
  );
}
